/* Shared primitives for every event-card shape: mobile list card (EventListCard), desktop list
   row (EventRow), and the flyers-view card (EventFlyerCard) - all built from the same status
   badge, CTA button, and no-flyer tile, per Main.dc.html/Desktop.dc.html/DesktopCards.dc.html. */

.noFlyerTile {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  padding: 10px;
  color: var(--text-on-dark);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.noFlyerType {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-on-dark-muted);
}
.noFlyerInitials {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 44px;
  line-height: 1;
}
.noFlyerCaption {
  font-size: 11px;
  color: var(--text-on-dark-faint);
}

.statusRow {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}
.statusDot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}
.statusUnpaid {
  color: var(--danger);
}

.cta {
  flex-shrink: 0;
  height: 44px;
  padding: 0 20px;
  border-radius: var(--r-10);
  text-decoration: none;
  font-weight: 700;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid transparent;
}
.ctaPrimary {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  color: var(--text-on-dark);
}
.ctaSecondary {
  background: var(--bg-panel);
  border-color: var(--border-4);
  color: var(--text-primary);
}
.ctaDark {
  background: var(--bg-dark);
  border-color: var(--bg-dark);
  color: var(--text-on-dark);
}
.ctaDanger {
  background: var(--danger);
  border-color: var(--danger);
  color: var(--text-on-dark);
}

/* `.cta` is an <a> - without this, global.css's `a:hover { color: var(--accent-text-hover) }`
   (higher specificity than any single `.ctaX` class) overrides every variant's text color on
   hover. Matches the legacy checkout.cfm button instead: background darkens slightly, text color
   never changes. */
.cta:hover {
  filter: brightness(0.92);
}
.ctaPrimary:hover {
  color: var(--text-on-dark);
}
.ctaSecondary:hover {
  color: var(--text-primary);
}
.ctaDark:hover {
  color: var(--text-on-dark);
}
.ctaDanger:hover {
  color: var(--text-on-dark);
}

.monthHeader {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}
.monthName {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  text-transform: uppercase;
}
.monthCount {
  font-size: 13px;
  color: var(--text-muted);
}

/* -- mobile list card (EventListCard, Main.dc.html) -- */
.mobileCard {
  background: var(--bg-panel);
  border-radius: var(--r-12);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.mobileCardTop {
  display: flex;
  gap: 12px;
}
.mobileFlyerLink {
  position: relative;
  display: block;
  width: 132px;
  height: 176px;
  flex-shrink: 0;
  border-radius: var(--r-8);
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(21, 23, 26, 0.18);
}
.mobileFlyerImg {
  display: block;
  width: 132px;
  height: 176px;
  object-fit: cover;
}
.mobileFlyerExpand {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 28px;
  height: 28px;
  border-radius: var(--r-pill);
  background: rgba(21, 23, 26, 0.82);
  color: var(--text-on-dark);
  display: flex;
  align-items: center;
  justify-content: center;
}
.mobileInfo {
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.mobileDates {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-text);
}
.mobileTitle {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 21px;
  line-height: 1.06;
  color: var(--text-primary);
}
.mobileTrack {
  font-size: 14px;
  color: var(--text-secondary);
}
.mobileCity {
  font-size: 13px;
  color: var(--text-muted);
}
.mobileStatusRow {
  margin-top: auto;
}
.mobileFooter {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--border-2);
}
.mobileMeta {
  font-size: 14px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

/* -- desktop list row (EventRow, Desktop.dc.html) -- */
.row {
  display: grid;
  grid-template-columns: 66px 74px 1fr 230px 150px;
  gap: 16px;
  align-items: center;
  padding: 14px 18px 14px 14px;
  border-bottom: 1px solid var(--border-3);
}
.rowFlyerLink {
  display: block;
  width: 66px;
  height: 88px;
  border-radius: 7px;
  overflow: hidden;
  text-decoration: none;
}
.rowFlyerImg {
  display: block;
  width: 66px;
  height: 88px;
  object-fit: cover;
}
.rowDateCol {
  text-align: center;
  border-right: 1px solid var(--border-2);
  padding-right: 12px;
}
.rowDow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--accent-text);
}
.rowDay {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 30px;
  line-height: 1;
}
.rowSpan {
  font-size: 12px;
  color: var(--text-muted);
}
.rowInfo {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.rowTitle {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 22px;
  line-height: 1.05;
  color: var(--text-primary);
}
.rowTrack {
  font-size: 15px;
  color: var(--text-secondary);
}
.rowTrackMuted {
  color: var(--text-muted);
}
.rowTags {
  display: flex;
  gap: 6px;
  margin-top: 3px;
}
.rowTag {
  padding: 3px 9px;
  border-radius: var(--r-pill);
  background: var(--bg-page);
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary);
}
.rowStatusCol {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.rowProgress {
  height: 6px;
  border-radius: var(--r-pill);
  background: var(--border-1);
  overflow: hidden;
}
.rowProgressFill {
  height: 6px;
  border-radius: var(--r-pill);
}
.rowMeta {
  font-size: 13px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

/* -- flyers-view card (EventFlyerCard, DesktopCards.dc.html; also used on mobile) -- */
.flyerCard {
  background: var(--bg-panel);
  border-radius: var(--r-14);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 10px rgba(21, 23, 26, 0.06);
}
.flyerCardImageLink {
  position: relative;
  display: block;
  height: 220px;
  overflow: hidden;
  background: var(--bg-dark);
  text-decoration: none;
}
.flyerCardBlur {
  position: absolute;
  left: -20px;
  top: -20px;
  width: calc(100% + 40px);
  height: calc(100% + 40px);
  object-fit: cover;
  filter: blur(20px) brightness(0.4);
}
.flyerCardImg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.flyerCardFullSize {
  position: absolute;
  right: 10px;
  bottom: 10px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 11px;
  border-radius: var(--r-pill);
  background: rgba(21, 23, 26, 0.85);
  color: var(--text-on-dark);
  font-size: 12px;
  font-weight: 600;
}
.flyerCardBody {
  flex-grow: 1;
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.flyerCardDates {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-text);
}
.flyerCardTitle {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 23px;
  line-height: 1.05;
  color: var(--text-primary);
}
.flyerCardTrack {
  font-size: 14px;
  color: var(--text-secondary);
}
.flyerCardCity {
  font-size: 13px;
  color: var(--text-muted);
}
.flyerCardFooter {
  margin-top: auto;
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.flyerCardMetaRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

@media (min-width: 1200px) {
  .flyerCardImageLink {
    height: 373px;
  }
}
